"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import FetchModal from "@/fetch-ui/FetchModal";
import Image from "next/image";

interface WelcomeNorthstarModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Partnership welcome shown when a lead is redirected from Northstar into the create wizard. Leads
 * with a co-brand "Nowadays × Northstar" lockup, then a short message and a single "Continue" CTA
 * that dismisses it. The modal itself animates in via FetchModal; its content simply appears.
 *
 * @param isOpen - Whether the modal is visible.
 * @param onClose - Dismisses the modal (the "Continue" action).
 */
export function WelcomeNorthstarModal({ isOpen, onClose }: WelcomeNorthstarModalProps) {
  return (
    <FetchModal
      isOpen={isOpen}
      onClose={onClose}
      size="auto"
      className="w-full max-w-xl overflow-hidden p-8"
      contentClassName="overflow-visible"
      floatingClose
      ariaLabel="Welcome to Nowadays, Northstar's venue sourcing partner"
    >
      <div className="flex flex-col">
        {/* Co-brand header — the collaboration moment. Bleeds to the modal edges via negative margins
            that cancel FetchModal's p-8; the modal's overflow-hidden clips it to the rounded corners. */}
        <div className="-mx-8 -mt-8 mb-8 flex items-center justify-center gap-4 border-b border-[#E6E8F5] bg-[#F3F4FC] px-8 py-9 sm:gap-7 sm:py-11">
          <Image
            src="/nowadays_black.svg"
            alt="Nowadays"
            width={150}
            height={42}
            priority
            className="h-8 w-auto sm:h-10"
          />
          <span
            aria-hidden="true"
            className="select-none text-2xl font-light leading-none text-[#8E97CC] sm:text-[2rem]"
          >
            &times;
          </span>
          <Image
            src="/northstar-meetings-logo.png"
            alt="Northstar Meetings Group"
            width={200}
            height={73}
            priority
            className="h-10 w-auto sm:h-12"
          />
        </div>

        {/* Message */}
        <div className="flex flex-col gap-6 text-left">
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#3646A4]">
              Venue sourcing partnership
            </p>
            <h2 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">Welcome to Nowadays</h2>
          </div>

          <p className="max-w-[52ch] text-[0.95rem] font-medium leading-relaxed text-gray-600">
            We&apos;ll connect you directly with hotel proposals for your event. Fill out a few quick details and
            we&apos;ll handle the rest.
          </p>

          <p className="max-w-[52ch] text-[0.95rem] font-medium leading-relaxed text-gray-600">
            This service is <span className="font-semibold text-gray-900">completely complimentary</span> through our
            partnership with Northstar Meetings Group. A member of our team will reach out to confirm your details and
            walk you through the platform.
          </p>

          <div className="pt-2">
            <FetchButton variant="primary" color="primary" size="lg" arrow onClick={onClose} className="w-full">
              Continue
            </FetchButton>
          </div>
        </div>
      </div>
    </FetchModal>
  );
}
